<template>
  <div class="min-h-screen bg-gradient-to-b from-green-50 via-emerald-50 to-white">
 
    <!-- 顶部产品介绍区域 -->
    <div class="product-hero relative bg-gradient-to-b from-green-50 via-emerald-50 to-green-100 py-20 px-4 sm:px-6 lg:px-8 z-10">
      
      <div class="max-w-7xl mx-auto relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          <!-- 左侧文字内容 -->
          <div class="space-y-8">
            <div class="space-y-6">
              <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-green-800 to-emerald-700 leading-tight">
                {{ t('product.title') }}
              </h1>
              <div class="relative">
                <p class="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed font-medium">
                  {{ t('product.description') }}
                </p>
                <div class="absolute -left-4 top-0 w-1 h-full bg-gradient-to-b from-green-500 to-emerald-600 rounded-full"></div>
              </div>
            </div>
          </div>
          
          <!-- 右侧图片 -->
          <div class="relative">
            <div class="relative">
              <!-- 主图片容器 -->
              <div class="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl bg-[#dededa]">
                <img
                  :src="backgroundImg"
                  class="relative z-10 w-full h-full object-cover"
                  :alt="t('product.title')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 主要内容区域 -->
    <div class="bg-gradient-to-b from-green-50 via-emerald-50 to-green-100 py-8 px-4 sm:px-6 lg:px-8 relative z-20">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <!-- 左侧分类导航 -->
          <div class="w-full lg:w-[240px] flex-shrink-0">
            <h2 class="text-base sm:text-lg font-medium mb-4 text-gray-900">产品分类</h2>
            <!-- 移动端水平滚动分类 -->
            <div class="lg:hidden">
              <div class="flex overflow-x-auto pb-2 scrollbar-hide">
                <div class="flex space-x-3 min-w-max">
                  <template v-for="(item,index) in productCategorys" :key="index">
                    <div 
                      class="whitespace-nowrap px-3 py-1.5 rounded-full border cursor-pointer flex-shrink-0 text-xs flex items-center justify-center min-h-[32px] max-w-[120px] overflow-hidden text-ellipsis"
                      :class="currentCategory === item ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-200'"
                      @click="handleProductCategory(item)"
                      :title="item"
                    >
                      <span class="truncate">{{ item }}</span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <!-- 桌面端垂直分类 -->
            <div class="hidden lg:block space-y-2">
              <template v-for="(item,index) in productCategorys" :key="index">
                <div 
                  class="text-sm px-3 py-2 rounded-lg cursor-pointer transition-colors"
                  :class="currentCategory === item ? 'bg-green-600 text-white' : 'text-green-800 hover:text-green-600 hover:bg-green-50'"
                  @click="handleProductCategory(item)"
                >
                  {{ item }}
                </div>
              </template>
            </div>
          </div>
          
          <!-- 右侧产品展示区 -->
          <div class="flex-1">
            <!-- 加载状态 -->
            <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <div v-for="n in 6" :key="n" class="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
                <div class="h-[200px] bg-gray-200"></div>
                <div class="p-4">
                  <div class="h-4 bg-gray-200 rounded mb-2"></div>
                  <div class="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div class="h-10 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
            
            <!-- 空状态 -->
            <div v-else-if="products.length === 0" class="text-center py-16">
              <div class="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 mb-2">暂无产品</h3>
              <p class="text-xs text-gray-500">该分类下暂时没有产品，请选择其他分类查看</p>
            </div>
            
            <!-- 产品网格 -->
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <!-- 产品卡片 -->
              <div
                v-for="(item, index) in products"
                :key="index"
                class="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col h-[320px] border border-gray-100"
              >
                <div class="h-[200px] overflow-hidden">
                  <img
                    :src="convertImageUrl(item.image)"
                    class="w-full h-full object-fill"
                    :alt="item.title"
                    @error="handleImageError"
                  />
                </div>
                <div class="p-4 flex flex-col flex-1">
                  <h3 class="text-xs sm:text-sm text-gray-800 mb-4 h-[40px] leading-5 overflow-hidden text-ellipsis line-clamp-2" :title="item.title">
                    {{ item.title }}
                  </h3>
                  <a-button
                    @click="navigateToContact"
                    type="primary"
                    class="w-full !rounded-button whitespace-nowrap bg-green-600 border-green-600 hover:bg-green-700 hover:border-green-700 mt-auto text-xs sm:text-sm"
                  >
                    {{ t('product.buttons.contactUs') }}
                  </a-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
  import { onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import tradeGoodsRecordApi from '@/api/tradeGoodsRecordApi.js'
  import bizDictApi from '@/api/bizDictApi.js'
  import backgroundImg from '@/assets/product/微信图片_20250909195001_1273_28.jpg'

  import { useI18n } from 'vue-i18n'

  const router = useRouter()

  // 使用国际化
  const { t, locale, messages } = useI18n()
  

  const productCategorys = ref([])
  const products = ref([])
  const productsAll = ref([])
  const currentCategory = ref('')
  const loading = ref(true)

  const convertImageUrl = (url) => {
    // 开发环境：直接使用完整 URL，方便本地调试
    if (import.meta.env.DEV) {
      return url
    }

    // 生产环境：仅在指定主机/端口时返回 pathname（适配反向代理）
    try {
      const u = new URL(url)
      if (u.hostname === '43.132.108.252' && u.port === '29000') {
        return u.pathname
      }
    } catch (e) {
      // 非法 URL 或解析失败，按原值返回
    }
    return url
  }

  const handleProductCategory = (category) => {
    loading.value = true
    currentCategory.value = category
    
    // 模拟加载延迟，提升用户体验
    setTimeout(() => {
      products.value = productsAll.value.filter(item => item.categoryName === currentCategory.value).map(item => {
        return {
          title: item.description,
          image: item.coverImg,
          id: item.id
        }
      })
      loading.value = false
    }, 300)
  }
  
  // 处理产品卡片点击
  const handleProductClick = (product) => {
    console.log('查看产品详情:', product)
    // 这里可以跳转到产品详情页面
  }
  
  // 处理联系我们按钮点击
  const handleContactUs = (product) => {
    console.log('联系我们:', product)
    // 这里可以打开联系表单或跳转到联系页面
  }

  // 导航到联系页面
  const navigateToContact = () => {
    router.push('/contact')
  }
  
  // 处理查看详情按钮点击
  const handleViewDetails = (product) => {
    console.log('查看详情:', product)
    // 这里可以打开产品详情模态框或跳转页面
  }
  
  // 处理图片加载错误
  const handleImageError = (event) => {
    // event.target.src = '/placeholder-image.jpg' // 设置默认图片
    event.target.style.backgroundColor = '#f3f4f6'
  }

  const initPageData = async ()=> {
    const dict =  await bizDictApi.gwTree()
    const children = dict.find(item=>item.dictValue === 'GOODS_CATEGORY').children
    productCategorys.value = [...new Set(children.map(item => item.dictLabel))]

    tradeGoodsRecordApi.tradeGoodsRecordAll().then( res => {
      productsAll.value = res
      currentCategory.value = productCategorys.value[0]
      products.value = productsAll.value.filter(item => item.categoryName === currentCategory.value).map(item => {
        return {
          title: item.description,
          image: item.coverImg,
          id: item.id
        }
      })
      loading.value = false
    }).catch(error => {
      console.error('加载产品数据失败:', error)
      loading.value = false
    })
  }



  onMounted( ()  =>  {
    initPageData()
  })

</script>
<style scoped>
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  /* 隐藏滚动条 */
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  
  /* 骨架加载动画 */
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  .animate-pulse {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  
  /* 响应式优化 */
  @media (max-width: 768px) {
    /* 移动端z-index优化 */
    .product-hero {
      z-index: 5 !important;
    }
    
    /* 移动端内容区域优化 */
    .bg-white {
      padding-top: 1.5rem;
      padding-bottom: 1.5rem;
    }
    
    /* 移动端产品卡片高度调整 */
    .h-\[320px\] {
      height: 280px;
    }
    
    /* 移动端图片高度调整 */
    .h-\[200px\] {
      height: 160px;
    }
    
    /* 移动端图片显示优化 */
    .h-\[200px\] img {
      object-fit: fill;
      object-position: center;
      width: 100%;
      height: 100%;
      border-radius: 0;
      background-color: #f5f5f5;
    }
    
    /* 移动端图片容器优化 */
    .h-\[200px\] {
      background-color: #f5f5f5;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    }
    
    /* 移动端产品卡片圆角优化 */
    .bg-white.rounded-lg {
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      border: 1px solid rgba(229, 231, 235, 0.8);
    }
    
    /* 移动端卡片悬停效果 */
    .bg-white.rounded-lg:active {
      transform: translateY(1px);
      box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.06);
    }
    
    /* 移动端字体大小优化 */
    .text-base {
      font-size: 0.875rem;
    }
    
    /* 移动端分类标签优化 */
    .lg\:hidden .flex.overflow-x-auto {
      scroll-behavior: smooth;
      -webkit-overflow-scrolling: touch;
    }
    
    /* 移动端分类标签间距优化 */
    .lg\:hidden .space-x-3 > * + * {
      margin-left: 0.75rem;
    }
    
    /* 移动端分类标签高度和文字优化 */
    .lg\:hidden .min-h-\[32px\] {
      height: 32px !important;
      min-height: 32px !important;
      line-height: 1.2;
      box-sizing: border-box;
    }
    
    /* 长文本处理 */
  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* 全局分类标签高度统一 */
.min-h-\[32px\] {
  height: 32px !important;
  min-height: 32px !important;
  max-height: 32px !important;
  box-sizing: border-box !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* 移动端分类标签强制统一高度 */
@media (max-width: 1024px) {
  .lg\:hidden .min-h-\[32px\] {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    line-height: 32px !important;
    padding: 0 12px !important;
    border-width: 1px !important;
  }
}
  
  /* 移动端内容区域层级优化 */
  @media (max-width: 1024px) {
    .bg-white {
      position: relative;
      z-index: 25;
    }
    
    /* 移动端容器padding优化 */
    .px-4 {
      padding-left: 1rem !important;
      padding-right: 1rem !important;
    }
    
    /* 移动端产品网格容器优化 */
    .grid.grid-cols-1.sm\:grid-cols-2.lg\:grid-cols-3 {
      margin-left: 0 !important;
      margin-right: 0 !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
  
  /* 平板端优化 */
  @media (min-width: 640px) and (max-width: 1024px) {
    /* 平板端产品网格优化 */
    .grid-cols-1.sm\:grid-cols-2.lg\:grid-cols-3 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
</style>
